import { useState } from 'react';
import type { SurveyDefinition } from '@webforge-survey/shared';
import { HelplinePage } from './HelplinePage';

interface ConsentScreenProps {
  definition: SurveyDefinition;
  onAccept: () => void;
}

/**
 * Notice d'information + porte de consentement (bloquant).
 *
 * - Case **non pré-cochée** (UI_DESIGN §5.1, RGPD_PLAN §2).
 * - Deux boutons : "Je consens et je commence" / "Je refuse".
 * - Refus → écran apaisant + helpline (zéro donnée transmise).
 * - Aucun nudge, aucune insistance, sortie nette.
 */
export function ConsentScreen({ definition, onAccept }: ConsentScreenProps) {
  const [accepted, setAccepted] = useState(false); // jamais pré-coché
  const [refused, setRefused] = useState(false);

  const idMode = definition.identityMode;

  if (refused) {
    return (
      <main
        className="min-h-screen bg-bg py-12 px-4"
        aria-labelledby="refused-title"
      >
        <div className="card max-w-prose mx-auto">
          <h1 id="refused-title" className="mb-3">
            Merci d'avoir pris le temps.
          </h1>
          <p className="text-text-muted mb-4">
            Vous avez choisi de ne pas commencer. C'est un choix tout à fait
            valide. Aucune information n'a été enregistrée.
          </p>
          <p className="text-text-muted">
            Vous pouvez fermer cet onglet. Si vous en ressentez le besoin,
            voici des ressources :
          </p>
        </div>
        <div className="mt-6">
          <HelplinePage variant="full" />
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-bg py-12 px-4"
      aria-labelledby="consent-title"
    >
      <div className="card max-w-prose mx-auto space-y-6">
        <header>
          <p className="text-sm text-text-muted">Avant de commencer</p>
          <h1 id="consent-title" className="mt-1">
            {definition.title}
          </h1>
        </header>

        {definition.notice && (
          <div className="prose-sm whitespace-pre-line text-text leading-relaxed">
            {definition.notice}
          </div>
        )}

        <section className="info-callout">
          <h2 className="text-base mb-2">Comment vos réponses sont traitées</h2>
          <ul className="text-sm space-y-2 list-disc pl-5">
            {idMode === 'anonymous' && (
              <>
                <li>
                  Cette enquête est <strong>anonyme</strong> : aucune adresse
                  e-mail, aucun identifiant, aucune adresse IP ne sont
                  enregistrés.
                </li>
                <li>
                  Vous pouvez interrompre et reprendre sur ce même appareil
                  grâce à un identifiant local qui ne contient aucune donnée
                  personnelle.
                </li>
                <li>
                  Une fois envoyées, vos réponses ne peuvent plus être retirées
                  individuellement (anonymat irréversible).
                </li>
              </>
            )}
            {idMode === 'optional' && (
              <>
                <li>
                  Par défaut, vos réponses sont <strong>anonymes</strong>.
                </li>
                <li>
                  À la fin du questionnaire, vous pourrez choisir librement
                  d'associer ou non votre identité. Aucun choix n'est valorisé
                  par rapport à l'autre.
                </li>
              </>
            )}
            {idMode === 'required' && (
              <>
                <li>
                  Cette enquête nécessite que vous soyez identifié·e. Un
                  consentement spécifique à l'identification vous sera demandé
                  à l'étape suivante.
                </li>
                <li>
                  Vous pourrez exercer vos droits (accès, rectification,
                  effacement, opposition) auprès du responsable de traitement.
                </li>
              </>
            )}
            <li>Aucun script tiers ni traceur n'est chargé sur cette page.</li>
          </ul>
        </section>

        <fieldset className="space-y-3">
          <legend className="sr-only">Consentement</legend>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-5 w-5 accent-accent flex-shrink-0"
              aria-describedby="consent-help"
            />
            <span className="text-text">
              J'ai lu la notice ci-dessus et je consens à participer à cette
              enquête dans les conditions décrites.
            </span>
          </label>
          <p id="consent-help" className="text-xs text-text-muted pl-8">
            Vous pouvez refuser sans aucune justification — vous quitterez la
            page sans laisser de trace.
          </p>
        </fieldset>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            className="btn-primary flex-1"
            disabled={!accepted}
            onClick={onAccept}
          >
            Je consens et je commence
          </button>
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={() => setRefused(true)}
          >
            Je refuse
          </button>
        </div>
      </div>
    </main>
  );
}
